/**
 * Turning a set's cards into a set's PRINTINGS, for the set view in Explore.
 *
 * A set is not its unique cards. Base Set is 102 cards and 408 printings, and
 * somebody who has only ever seen the 102 has no way to learn that the Charizard
 * in their hand is one of four - which is the fact the owner wanted the grid to
 * teach: "Default in a set is all variants."
 *
 * WHY THIS IS NOT components/wish/setSlots.js.
 *
 * That file builds tiles for the Wish tab's master set grid, where a tile is a
 * slot somebody is chasing: it carries `owned` and `printing_unknown` from the
 * server's own ownership rule and drops a card to draw the shape of the hole.
 * The set view in Explore is a browser rather than a chase list. Its ownership
 * has always been the page's own `cards` and `wishListItems` - that is what its
 * All / Owned / Wish chips mean - so pulling in the chase list's resolved
 * ownership would put two different answers to "do I have this" on one screen.
 * What is shared is the READ (`wish_set_slot_grid`, through fetchSlotGrid) and
 * the printing vocabulary (components/wish/printings.js), so one printing is one
 * word and one colour in both places.
 */

import {
  slotPrintingKey, printingLabel, printingShortLabel, printingColor, printingTextColor,
  isPlainFinish,
} from '../wish/printings';

/**
 * One tile per (card, printing), in the card order the sort asked for.
 *
 * @param {object} input
 * @param {object[]} input.cards  the set's cards, already searched, chipped and
 *   sorted by the page. Each needs `id` in catalogue form, which is what the
 *   slot rows key on.
 * @param {object[]} input.slots  rows from `wish_set_slot_grid` for this set.
 * @param {string|null} input.printing  a printing key to narrow to, null for
 *   every printing - which is the default, because it is what a set IS.
 * @returns {object[]} tiles carrying the card, the badge, THIS printing's
 *   picture and THIS printing's price. `priceIsForPrinting` false means the figure is the card's rather than
 *   this print run's, which the tile marks with a tilde: a shadowless Charizard
 *   and a plain one are not the same object.
 */
export function buildSetPrintingTiles({ cards = [], slots = [], printing = null, keepUnslotted = false }) {
  // How many printings each card HAS, counted before the filter throws the
  // others away. A grid narrowed to one printing still has to say the card has
  // four, or narrowing it turns a shelf of variants into a shorter list that
  // teaches nothing.
  const totalByCard = new Map();
  for (const slot of slots) {
    totalByCard.set(slot.card_id, (totalByCard.get(slot.card_id) || 0) + 1);
  }

  const byCard = new Map();
  for (const slot of slots) {
    if (printing && slotPrintingKey(slot) !== printing) continue;
    const list = byCard.get(slot.card_id);
    if (list) list.push(slot);
    else byCard.set(slot.card_id, [slot]);
  }

  const tiles = [];
  for (const card of cards) {
    const rows = byCard.get(card.id);
    if (!rows) {
      /* A set the printing model does not cover - TCG Pocket, or a read that
         failed - still gets one tile per card, unbadged. An empty grid would be
         a worse answer than a grid with no badges on it. Once the set HAS slots,
         a card with none of them was excluded by the chosen printing and is
         meant to be absent.

         `keepUnslotted` is for a MIXED list - a search result, the flat
         catalogue - where a card with no printing rows sits beside cards that
         have them. There it is not excluded by anything; it is simply a card
         the printing model has not described (the Collectr-sourced sets), and
         it keeps its one tile. */
      if (slots.length === 0 || keepUnslotted) {
        tiles.push({ key: card.id, card, badge: null, price: null,
                     priceIsForPrinting: false, image: card.image_url || null,
                     // No slot row, so nothing is known about printings here
                     // and the caller falls back to its own card-level answer.
                     owned: null, printingUnknown: false, printingCount: 1 });
      }
      continue;
    }
    // A card with one plain printing has nothing to be told apart from. A
    // "Standard" badge on every card in a modern set is 200 repetitions of
    // nothing, and it made a one-card promo set look like it had a variant.
    const plainOnly = rows.length === 1
      && !rows[0].printing_subtype
      && !rows[0].printing_foil
      && isPlainFinish(rows[0].printing_type)
      && !(rows[0].printing_stamps || []).filter(Boolean).length;
    for (const slot of inPrintingOrder(rows)) {
      tiles.push({
        key: `${card.id}::${slotPrintingKey(slot)}`,
        card,
        badge: plainOnly ? null : {
          subtype: slot.printing_subtype || null,
          stamps: slot.printing_stamps || [],
          foil: slot.printing_foil || null,
          // The finish rides with the rest of the printing, so anything that
          // reads a badge - the grid, a scan, a card screen - gets told which
          // one of these a tap meant.
          type: slot.printing_type || 'normal',
          label: printingShortLabel(slot.printing_subtype, slot.printing_stamps, slot.printing_foil, slot.printing_type),
          full: printingLabel(slot.printing_subtype, slot.printing_stamps, slot.printing_foil, slot.printing_type),
          color: printingColor(slot.printing_subtype, slot.printing_stamps, slot.printing_foil, slot.printing_type),
          textColor: printingTextColor(slot.printing_subtype, slot.printing_stamps, slot.printing_foil, slot.printing_type),
        },
        price: slot.near_mint ?? null,
        priceIsForPrinting: slot.price_is_for_printing === true,
        // Ownership of THIS printing, which is not the same question as
        // ownership of the card. A plain Holo in the collection does not make
        // the 1st Edition owned. `printingUnknown` is the honest middle: the
        // card is held but its print run was never recorded, so it has been
        // inferred onto one printing rather than claimed on all of them.
        owned: slot.owned === true,
        printingUnknown: slot.printing_unknown === true,
        // Every printing this card has, not just the one being drawn.
        printingCount: totalByCard.get(card.id) || 1,
        // This printing's own picture. It is the card's picture when the
        // printing shares one, which is right: plain holo and reverse holo are
        // the same artwork with a different finish. Null only when the
        // catalogue has no variant row at all, and then the card's stands in.
        image: slot.image_url || card.image_url || null,
      });
    }
  }
  return tiles;
}

/**
 * What ONE printing is worth, summed over its own slots.
 *
 * The full set value stays the headline everywhere else, including inside the
 * set while every printing is showing. A grid narrowed to 1st Edition beside a
 * number that counts all four print runs is a number contradicting the screen
 * it sits on, which is the whole reason this exists.
 *
 * `approx` means at least one slot quoted the card's figure because its own
 * printing has no price yet, so the sum is about right rather than exact - the
 * same claim the tilde on a tile makes.
 */
export function sumPrintingValue(slots = [], printing = null) {
  if (!printing) return null;
  let total = 0;
  let priced = 0;
  let count = 0;
  let approx = false;
  for (const slot of slots) {
    if (slotPrintingKey(slot) !== printing) continue;
    count += 1;
    if (slot.near_mint == null) continue;
    total += Number(slot.near_mint);
    priced += 1;
    if (slot.price_is_for_printing !== true) approx = true;
  }
  return { total, priced, slots: count, approx };
}

/** How many slots one printing holds, for the filter sheet's own counts. */
export function countPrintingSlots(slots = [], printing = null) {
  let count = 0;
  for (const slot of slots) {
    if (slotPrintingKey(slot) === printing) count += 1;
  }
  return count;
}

function inPrintingOrder(rows) {
  return [...rows].sort((a, b) => (
    // The plain card first, then its finishes. Somebody looking for the
    // ordinary one should not have to read past the reverse to find it.
    (isPlainFinish(a.printing_type) ? 0 : 1) - (isPlainFinish(b.printing_type) ? 0 : 1)
    || printingLabel(a.printing_subtype, a.printing_stamps, a.printing_foil, a.printing_type)
      .localeCompare(printingLabel(b.printing_subtype, b.printing_stamps, b.printing_foil, b.printing_type))
  ));
}
